/**
 * useCoupleFlow — state machine for the 7-screen Couple Portrait flow.
 *
 * States: idle → initiating → waiting_partner → both_confirmed → generating → completed
 *         Any state → failed | cancelled
 *
 * Persists couplePortraitId in localStorage and resumes in-flight sessions on mount.
 * Polls every 15s while waiting_partner or generating.
 */

import { useReducer, useEffect, useRef, useCallback } from 'react';
import { apiFetch } from '../lib/api-client';
import { useAuth } from '../store/auth';

// --------------------------------------------------------------------------
// Types
// --------------------------------------------------------------------------

export type CoupleFlowState =
  | 'idle'
  | 'initiating'
  | 'waiting_partner'
  | 'both_confirmed'
  | 'generating'
  | 'completed'
  | 'cancelled'
  | 'failed';

export interface CoupleFlowData {
  state: CoupleFlowState;
  couplePortraitId: string | null;
  expiresAt: string | null;
  portraitR2Key: string | null;
  coupleName: string | null;
  archetypeBlend: string | null;
  partnerUsername: string | null;
  error: string | null;
}

export type CoupleEvent =
  | { type: 'INITIATE'; partnerUsername: string }
  | { type: 'INITIATED'; couplePortraitId: string; expiresAt: string; partnerUsername: string }
  | { type: 'CANCEL' }
  | { type: 'PARTNER_CONFIRMED' }
  | { type: 'GENERATION_STARTED' }
  | { type: 'COMPLETED'; portraitR2Key: string; coupleName: string; archetypeBlend: string }
  | { type: 'FAILED'; error: string }
  | { type: 'RESET' }
  | { type: 'RESTORE'; data: Partial<CoupleFlowData> };

export interface UseCoupleFlowReturn extends CoupleFlowData {
  send: (event: CoupleEvent) => void;
  initiate: (partnerUsername: string) => Promise<void>;
  cancel: () => Promise<void>;
}

// --------------------------------------------------------------------------
// Initial state
// --------------------------------------------------------------------------

const INITIAL: CoupleFlowData = {
  state: 'idle',
  couplePortraitId: null,
  expiresAt: null,
  portraitR2Key: null,
  coupleName: null,
  archetypeBlend: null,
  partnerUsername: null,
  error: null,
};

// --------------------------------------------------------------------------
// Reducer
// --------------------------------------------------------------------------

function coupleReducer(data: CoupleFlowData, event: CoupleEvent): CoupleFlowData {
  switch (event.type) {
    case 'INITIATE':
      return { ...data, state: 'initiating', partnerUsername: event.partnerUsername, error: null };

    case 'INITIATED':
      return {
        ...data,
        state: 'waiting_partner',
        couplePortraitId: event.couplePortraitId,
        expiresAt: event.expiresAt,
        partnerUsername: event.partnerUsername,
        error: null,
      };

    case 'CANCEL':
      return { ...INITIAL, state: 'cancelled' };

    case 'PARTNER_CONFIRMED':
      return { ...data, state: 'both_confirmed' };

    case 'GENERATION_STARTED':
      return { ...data, state: 'generating' };

    case 'COMPLETED':
      return {
        ...data,
        state: 'completed',
        portraitR2Key: event.portraitR2Key,
        coupleName: event.coupleName,
        archetypeBlend: event.archetypeBlend,
        error: null,
      };

    case 'FAILED':
      return { ...data, state: 'failed', error: event.error };

    case 'RESET':
      return { ...INITIAL };

    case 'RESTORE':
      return { ...data, ...event.data };

    default:
      return data;
  }
}

// --------------------------------------------------------------------------
// Status response shape from GET /api/v1/sku/couple-portrait/status/:id
// --------------------------------------------------------------------------

interface StatusResponse {
  status: string;          // 'waiting_partner' | 'both_confirmed' | 'generating' | 'completed' | 'cancelled' | 'failed'
  expiresAt?: string;
  partnerUsername?: string;
  portraitR2Key?: string;
  coupleName?: string;
  archetypeBlend?: string;
  error?: string;
}

// --------------------------------------------------------------------------
// Hook
// --------------------------------------------------------------------------

export function useCoupleFlow(skuCode: string = 'couple_portrait'): UseCoupleFlowReturn {
  const [data, dispatch] = useReducer(coupleReducer, INITIAL);
  const jwt = useAuth((s) => s.jwt);
  const userId = useAuth((s) => s.user?.id ?? null);

  const storageKey = userId ? `coupleFlow:${userId}` : null;

  // ---- Resume on mount ----
  useEffect(() => {
    if (!jwt || !storageKey) return;

    const stored = localStorage.getItem(storageKey);
    if (!stored) return;

    let storedId: string | null = null;
    try {
      const parsed = JSON.parse(stored) as { couplePortraitId?: string };
      storedId = parsed.couplePortraitId ?? null;
    } catch {
      return;
    }

    if (!storedId) return;

    void (async () => {
      try {
        const res = await apiFetch(`/api/v1/sku/couple-portrait/status/${storedId}`, jwt);
        if (!res.ok) return;
        const json = (await res.json()) as StatusResponse;

        const statusMap: Record<string, CoupleFlowState> = {
          waiting_partner: 'waiting_partner',
          both_confirmed: 'both_confirmed',
          generating: 'generating',
          completed: 'completed',
          cancelled: 'cancelled',
          failed: 'failed',
        };

        const restored: Partial<CoupleFlowData> = {
          couplePortraitId: storedId,
          state: statusMap[json.status] ?? 'idle',
          expiresAt: json.expiresAt ?? null,
          partnerUsername: json.partnerUsername ?? null,
          portraitR2Key: json.portraitR2Key ?? null,
          coupleName: json.coupleName ?? null,
          archetypeBlend: json.archetypeBlend ?? null,
          error: json.error ?? null,
        };

        dispatch({ type: 'RESTORE', data: restored });
      } catch {
        // Ignore network errors on resume
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally run once on mount

  // ---- Polling while waiting_partner or generating ----
  const stateRef = useRef(data.state);
  stateRef.current = data.state;
  const coupleIdRef = useRef(data.couplePortraitId);
  coupleIdRef.current = data.couplePortraitId;

  useEffect(() => {
    const shouldPoll = data.state === 'waiting_partner' || data.state === 'generating' || data.state === 'both_confirmed';
    if (!shouldPoll || !jwt || !data.couplePortraitId) return;

    const intervalId = setInterval(async () => {
      const id = coupleIdRef.current;
      if (!id) return;
      try {
        const res = await apiFetch(`/api/v1/sku/couple-portrait/status/${id}`, jwt);
        if (!res.ok) return;
        const json = (await res.json()) as StatusResponse;

        if (json.status === 'completed' && json.portraitR2Key) {
          dispatch({
            type: 'COMPLETED',
            portraitR2Key: json.portraitR2Key,
            coupleName: json.coupleName ?? '',
            archetypeBlend: json.archetypeBlend ?? '',
          });
        } else if (json.status === 'both_confirmed') {
          dispatch({ type: 'PARTNER_CONFIRMED' });
        } else if (json.status === 'generating') {
          dispatch({ type: 'GENERATION_STARTED' });
        } else if (json.status === 'failed') {
          dispatch({ type: 'FAILED', error: json.error ?? 'Generation failed' });
        } else if (json.status === 'cancelled') {
          dispatch({ type: 'CANCEL' });
        }
      } catch {
        // Ignore poll errors
      }
    }, 15000);

    return () => clearInterval(intervalId);
  }, [data.state, data.couplePortraitId, jwt]);

  // ---- Actions ----

  const initiate = useCallback(async (partnerUsername: string): Promise<void> => {
    if (!jwt) return;
    dispatch({ type: 'INITIATE', partnerUsername });
    try {
      const res = await apiFetch('/api/v1/sku/couple-portrait/initiate', jwt, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ partnerUsername, skuCode }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        dispatch({ type: 'FAILED', error: body.error ?? `Request failed: ${res.status}` });
        return;
      }
      const json = (await res.json()) as { couplePortraitId: string; expiresAt: string };
      if (storageKey) {
        try {
          localStorage.setItem(storageKey, JSON.stringify({ couplePortraitId: json.couplePortraitId }));
        } catch {
          // Storage quota or private mode — ignore
        }
      }
      dispatch({
        type: 'INITIATED',
        couplePortraitId: json.couplePortraitId,
        expiresAt: json.expiresAt,
        partnerUsername,
      });
    } catch {
      dispatch({ type: 'FAILED', error: 'network_error' });
    }
  }, [jwt, storageKey, skuCode]);

  const cancel = useCallback(async (): Promise<void> => {
    const id = coupleIdRef.current;
    if (!jwt || !id) {
      dispatch({ type: 'CANCEL' });
      return;
    }
    try {
      await apiFetch('/api/v1/sku/couple-portrait/cancel', jwt, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ couplePortraitId: id }),
      });
    } catch {
      // Ignore cancel errors — reset state regardless
    }
    if (storageKey) {
      try {
        localStorage.removeItem(storageKey);
      } catch {
        // ignore
      }
    }
    dispatch({ type: 'CANCEL' });
  }, [jwt, storageKey]);

  return {
    ...data,
    send: dispatch,
    initiate,
    cancel,
  };
}
